/**
 * 청크 로드 실패 자체 복구 — 캐시버스팅 reload 런타임 net (#198).
 *
 * 배경: 배포 3중 풋건(index.html 캐시 헤더 없음 + S3 `sync --delete` + SPA fallback 200)이 겹치면
 * 클라 WebView 가 캐시한 옛 index.html 이 이미 삭제된 해시 청크를 요청하고, 그 응답이
 * `200 text/html`(index.html fallback)로 떨어져 lazy 라우트 동적 import 가 실패한다.
 * stale 은 서버가 아니라 클라 캐시에만 존재하므로, 캐시버스팅 URL(`?_r=<ts>`)로 replace 하면
 * 캐시 미스 → 최신 index.html 재취득 → 최신 청크 참조로 복구된다.
 * 근본 방지(캐시 헤더 정비)는 인프라 트랙 — 이 모듈은 "터졌을 때 매끄럽게 복구"만 담당한다.
 *
 * 설계 (단일 수렴 funnel):
 * - 감지 지점 4곳(vite:preloadError / unhandledrejection / error / RouteErrorBoundary didCatch)이
 *   전부 {@link tryRecover} 하나로 모인다. 리로드 여부 판정은 이 함수가 단독 소유한다.
 * - `vite:preloadError` 에는 preventDefault 하지 않는다 — Vite preload helper 는 defaultPrevented 면
 *   throw 를 생략해 lazy 팩토리가 undefined 모듈로 resolve 되고, 그 후속 렌더 에러는 청크 패턴이
 *   아니라서 boundary 가 rethrow(루트 언마운트)하게 된다. 원본 TypeError 를 그대로 React 로
 *   전파시켜 RouteErrorBoundary 가 상태 기반으로 렌더(복구 대기 null / 가드 안내)를 결정한다.
 * - 루프 가드 2계층: (같은 문서) {@link reloadInFlight} 모듈 플래그가 replace 예약 후 unload 전까지의
 *   중복 발화를 "이미 복구 중"으로 흡수 / (문서 교체 후) sessionStorage 시각으로 리로드 직후
 *   재실패를 판정해 무한 리로드를 차단한다.
 */

const RELOAD_AT_KEY = "chunk-reload-at";
const GUARD_WINDOW_MS = 10_000;
/** 캐시버스팅 쿼리 키 — DeepLinkCapture 는 `deeplink`·`share_code` 키만 다루므로 상호 무간섭. */
const BUST_PARAM = "_r";

/**
 * 청크 로드 실패 메시지 패턴 — Chromium(AOS WebView) / WebKit(iOS WKWebView, "Importing a module
 * script failed") / Firefox 를 모두 커버한다. 엔진별 문구가 달라 하나라도 빠지면 그 플랫폼만
 * 복구가 죽는다.
 */
const CHUNK_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError|error loading dynamically imported module/i;

/** 같은 문서 내 중복 발화 흡수 — replace 예약 후 unload 전까지 들어오는 청크 에러는
 *  "재실패"가 아니라 "이미 복구 중"이다 (sessionStorage 가드는 문서 교체 후 판정 전용). */
let reloadInFlight = false;

/** 이번 부팅 URL 에 `_r` 이 있었는지 — sessionStorage 불가 환경(프라이빗 모드 등)에서
 *  문서 간 가드의 폴백 신호로 쓴다 (없으면 그런 환경에서 무한 리로드 루프가 가능해진다). */
let bootHadBustParam = false;

export function isChunkLoadError(err: unknown): boolean {
  if (err == null) return false;
  const message = err instanceof Error ? err.message : String(err);
  return CHUNK_ERROR_PATTERN.test(message);
}

/** 마지막 복구 리로드 시각. null = 스토리지 접근 불가(가드 폴백 경로로 분기). */
function readLastReloadAt(): number | null {
  try {
    const raw = sessionStorage.getItem(RELOAD_AT_KEY);
    if (raw === null) return 0;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  } catch {
    return null;
  }
}

function markReloadAttempt(): void {
  try {
    sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()));
  } catch {
    // 스토리지 불가 — 문서 간 가드는 bootHadBustParam 폴백이 담당한다.
  }
}

function isWithinGuardWindow(): boolean {
  const last = readLastReloadAt();
  // 스토리지 불가 환경: 이번 부팅이 이미 복구 리로드였다면(_r 부착 진입) 가드로 간주 — 루프 차단.
  if (last === null) return bootHadBustParam;
  return Date.now() - last < GUARD_WINDOW_MS;
}

/** RouteErrorBoundary 렌더 분기용 순수 읽기 — 부작용 없음(render 에서 호출 가능). */
export function canAutoRecover(): boolean {
  return reloadInFlight || !isWithinGuardWindow();
}

/**
 * 캐시버스팅 리로드 — pathname·기존 쿼리·해시 보존, `_r` 만 갱신. `replace` 라 히스토리 오염 없음.
 * 가드를 검사하지 않는다 — 안내 화면 [다시 시도하기](수동 트리거) 전용 직접 호출 경로.
 */
export function forceRecoveryReload(): void {
  reloadInFlight = true;
  markReloadAttempt();
  const url = new URL(window.location.href);
  url.searchParams.set(BUST_PARAM, String(Date.now()));
  window.location.replace(url.pathname + url.search + url.hash);
}

/**
 * 자동 복구 시도 — 모든 감지 지점이 이 함수 하나로 수렴한다.
 * @returns true = 복구 진행 중(이미 예약됐거나 방금 예약) / false = 가드 발동(리로드하지 않음 —
 *          안내는 RouteErrorBoundary 가 담당).
 */
export function tryRecover(): boolean {
  if (reloadInFlight) return true;
  if (isWithinGuardWindow()) return false;
  forceRecoveryReload();
  return true;
}

/**
 * 부트스트랩 최상단(main.tsx) 1회 등록 — React 트리 밖이라 렌더 실패와 무관하게 동작한다.
 * `vite:preloadError` 는 prod 빌드 전용 발화(dev 는 네이티브 ESM) — dev 에서는 dormant.
 */
export function registerChunkReloadRecovery(): void {
  // 복구 부팅 성공 표지: `_r` 은 소비 후 즉시 strip (가드 시각은 유지 — 10s 윈도우 자연 만료).
  const url = new URL(window.location.href);
  if (url.searchParams.has(BUST_PARAM)) {
    bootHadBustParam = true;
    url.searchParams.delete(BUST_PARAM);
    window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
  }

  // ① 주 경로: Vite preload helper. preventDefault 금지 — 모듈 헤더 주석의 undefined-resolve 함정 참조.
  window.addEventListener("vite:preloadError", () => {
    tryRecover();
  });

  // ②③ 백업망: helper 를 타지 않는 실패. 복구 예약 시에만 preventDefault(내비게이션 중 기본
  // 콘솔 에러 처리 억제) — 가드(false)면 기본 동작을 유지해 관측 가능성을 남긴다.
  window.addEventListener("unhandledrejection", (event) => {
    if (isChunkLoadError(event.reason) && tryRecover()) event.preventDefault();
  });
  window.addEventListener("error", (event) => {
    if (isChunkLoadError(event.error ?? event.message) && tryRecover()) event.preventDefault();
  });
}
